/**
 * Side-by-side comparison (spec section 16).
 *
 * A horizontally-scrollable table with a sticky metric column, so it works on a
 * phone without shrinking a desktop layout down. The best value in each row is
 * highlighted with a symbol as well as colour (section 29), and rows where no
 * product has data say so instead of highlighting an arbitrary winner.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, compareProducts } from '@/api/client';
import {
  METRICS,
  METRIC_DIRECTION,
  computeMetrics,
  type MetricId,
} from '@/lib/nutrition/calculations';
import { analyse, sortOptionById } from '@/lib/nutrition/ranking';
import type { Product } from '@/types/product';
import { formatCurrency, formatMetricValue, formatPackage, metricUnit } from '@/lib/format';
import { useAppState } from '@/state/AppState';
import { Notice } from '@/components/Panels';
import { RankingBarChart } from '@/components/charts/RankingBarChart';
import { MacroBar } from '@/components/MacroBar';
import { ScanPanel } from '@/components/scanner/ScanPanel';
import { ScannedItems } from '@/components/scanner/ScannedItems';
import {
  newScanId,
  toProduct,
  type ScanEntry,
  type ScannedItem,
} from '@/lib/scanner/calculate';
import { readSnapshot, snapshotKey } from '@/lib/leaderboard-cache';
import type { NutritionLabel } from '@/lib/scanner/types';

/**
 * Two at a time, as asked.
 *
 * Also the honest limit for the layout: three columns of a metric table do not
 * fit a phone without either shrinking the numbers or hiding some of them, and
 * a comparison you have to scroll sideways to read is not a comparison.
 */
const MAX_SCANS = 2;

/** Rows are ordered by the spec's list in section 16. */
const ROWS: MetricId[] = [
  'price',
  'packageSize',
  'pricePer100g',
  'pricePer100ml',
  'kcal100',
  'protein100',
  'fibre100',
  'sugar100',
  'proteinPerDollar',
  'kcalPerDollar',
  'pricePer10gProtein',
  'pricePer100kcal',
  'proteinPctCalories',
  'carbPctCalories',
  'fatPctCalories',
];

export function ComparePage(): React.JSX.Element {
  const { compareIds, removeCompare, clearCompare, criteria } = useAppState();

  /*
   * Scanned items live in component state and nowhere else.
   *
   * Not localStorage, not IndexedDB, not the database — the brief is that a
   * scan exists only for the comparison the user is making right now, and the
   * simplest way to guarantee that is to have no persistence code at all.
   * Leaving the page unmounts this, and the items are gone.
   */
  const [scans, setScans] = useState<ScannedItem[]>([]);
  const [scanning, setScanning] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [failed, setFailed] = useState<Array<{ id: string; reason: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (compareIds.length < 2) {
      setProducts([]);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    compareProducts(compareIds, controller.signal)
      .then((result) => {
        setProducts(result.data.products);
        setFailed(result.data.failed);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : 'Could not load the comparison.');
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [compareIds]);

  /*
   * The comparison set for the ranking preview.
   *
   * Read from the leaderboard's own snapshot rather than fetched: it is
   * already on the device, so the placement costs no request, no database read
   * and works with no connection. It is also exactly what the user last saw
   * ranked, which makes "where would this land" a question about something
   * they recognise.
   */
  const ranked = useMemo(() => {
    const snap = readSnapshot(snapshotKey(criteria, null, 'human'));
    return { products: snap?.products ?? [], eligible: snap?.payload.eligible ?? null };
  }, [criteria]);

  const addScan = useCallback((label: NutritionLabel, entry: ScanEntry) => {
    setScans((current) => {
      if (current.length >= MAX_SCANS) return current;
      const now = new Date().toISOString();
      const id = newScanId(current.length + Date.now());
      return [...current, { id, label, entry, product: toProduct(id, label, entry, now), scannedAt: now }];
    });
    setScanning(false);
  }, []);

  const rows = useMemo(
    () => analyse(products, computeMetrics, sortOptionById('protein_per_dollar')),
    [products],
  );

  // Which product wins each metric. Rows where nobody has data get `null`,
  // so we never highlight a "winner" by default.
  const winners = useMemo(() => {
    const map = new Map<MetricId, string | null>();
    for (const metric of ROWS) {
      const candidates = rows
        .map((r) => ({ id: r.product.id, value: r.metrics.values[metric]?.value ?? null }))
        .filter((c): c is { id: string; value: number } => c.value !== null);
      if (candidates.length === 0) {
        map.set(metric, null);
        continue;
      }
      const best = candidates.reduce((a, b) =>
        METRIC_DIRECTION[metric] === 'higher'
          ? b.value > a.value
            ? b
            : a
          : b.value < a.value
            ? b
            : a,
      );
      // A tie across every product is not a win worth highlighting.
      map.set(metric, candidates.every((c) => c.value === best.value) ? null : best.id);
    }
    return map;
  }, [rows]);

  // Only show a per-100 g row if some product is sold by weight, and likewise
  // for volume — otherwise the table fills with N/A for no reason.
  const visibleRows = ROWS.filter((metric) => {
    if (metric === 'pricePer100g') return rows.some((r) => r.metrics.packageKind === 'mass');
    if (metric === 'pricePer100ml') return rows.some((r) => r.metrics.packageKind === 'volume');
    return true;
  });

  const currency = rows[0]?.metrics.currency ?? null;

  return (
    <>
      <div className="sheet-head">
        <h1>Comparison</h1>
        {compareIds.length > 0 && (
          <button type="button" className="btn btn-sm" onClick={clearCompare}>
            Clear all
          </button>
        )}
      </div>

      {/*
        Scanning sits at the top because it is the thing you do while standing
        in the shop holding a packet; the catalogue comparison is the thing you
        do afterwards.
      */}
      <section className="scan-entry-point">
        <div className="scan-entry-copy">
          <h2>Holding something not in the catalogue?</h2>
          <p>
            Photograph its nutrition label and add your price, and we will compare it against the
            rankings.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={scans.length >= MAX_SCANS || scanning}
          onClick={() => setScanning(true)}
        >
          {scans.length === 0 ? 'Scan a label' : 'Scan another'}
        </button>
        {scans.length >= MAX_SCANS && (
          <p className="scan-field-note">
            Two at a time. Remove one to scan another.
          </p>
        )}
      </section>

      {scanning && (
        <ScanPanel
          currency={currency ?? 'SGD'}
          onDone={addScan}
          onClose={() => setScanning(false)}
        />
      )}

      <ScannedItems
        items={scans}
        catalogue={ranked.products}
        eligible={ranked.eligible}
        onRemove={(id) => setScans((cur) => cur.filter((s) => s.id !== id))}
        onUpdate={(id, entry) =>
          setScans((cur) =>
            cur.map((s) =>
              // Rebuild the product from the new entry, so every metric on the
              // card and every placement follows the edit immediately.
              s.id === id
                ? { ...s, entry, product: toProduct(s.id, s.label, entry, s.scannedAt) }
                : s,
            ),
          )
        }
      />

      {compareIds.length < 2 && (
        <div className="empty-state">
          <h2>Select products to compare</h2>
          <p>
            Add at least two products from the rankings or search to see them side by side — or
            scan a label above.
          </p>
          <Link className="btn btn-primary" to="/">
            Browse the rankings
          </Link>
        </div>
      )}

      {error && (
        <Notice kind="error" title="Comparison failed.">
          {error}
        </Notice>
      )}

      {failed.length > 0 && (
        <Notice kind="warn" title="Some products could not be loaded.">
          <ul>
            {failed.map((f) => (
              <li key={f.id}>
                {f.id}: {f.reason}
              </li>
            ))}
          </ul>
        </Notice>
      )}

      {loading && <div className="spinner-row">Loading comparison…</div>}

      {rows.length > 0 && (
        <>
          <div className="compare-scroll">
            <table className="compare-table">
              <caption className="visually-hidden">
                Nutritional value comparison. The best value in each row is marked with a star.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Metric</th>
                  {rows.map((row) => (
                    <th scope="col" key={row.product.id}>
                      <Link to={`/product/${encodeURIComponent(row.product.id)}`}>
                        {row.product.name}
                      </Link>
                      <span className="compare-formula">
                        {row.product.brand ?? 'Unbranded'} ·{' '}
                        {row.product.store?.name ?? 'Unknown store'}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ marginTop: '0.3rem' }}
                        onClick={() => removeCompare(row.product.id)}
                      >
                        Remove
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">
                    Package
                    <span className="compare-formula">net quantity</span>
                  </th>
                  {rows.map((row) => (
                    <td key={row.product.id}>{formatPackage(row.product.quantity)}</td>
                  ))}
                </tr>

                {visibleRows.map((metric) => {
                  const winner = winners.get(metric) ?? null;
                  return (
                    <tr key={metric}>
                      <th scope="row">
                        {METRICS[metric].label}
                        <span className="compare-formula">
                          {metricUnit(metric, currency)} · {METRICS[metric].formula}
                        </span>
                      </th>
                      {rows.map((row) => {
                        const value = row.metrics.values[metric];
                        const isBest = winner === row.product.id;
                        return (
                          <td
                            key={row.product.id}
                            className={isBest ? 'is-best' : undefined}
                            title={value?.unavailableReason ?? undefined}
                          >
                            {isBest && (
                              <span className="best-mark" aria-label="Best value">
                                ★
                              </span>
                            )}
                            {value?.value === null || value === undefined ? (
                              <span className="na">N/A</span>
                            ) : (
                              formatMetricValue(metric, value, currency, row.metrics.packageKind)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}

                <tr>
                  <th scope="row">
                    Store
                    <span className="compare-formula">location</span>
                  </th>
                  {rows.map((row) => (
                    <td key={row.product.id}>
                      {row.product.store?.name ?? 'N/A'}
                      <br />
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {row.product.store?.location ?? ''}
                      </span>
                    </td>
                  ))}
                </tr>

                <tr>
                  <th scope="row">Value score</th>
                  {rows.map((row) => (
                    <td key={row.product.id}>
                      {row.score.score === null ? (
                        <span className="na" title={row.score.unavailableReason ?? undefined}>
                          N/A
                        </span>
                      ) : (
                        row.score.score
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', margin: '0.5rem 0 1rem' }}>
            ★ marks the best value in each row. N/A means the metric could not be calculated from
            available data — it is not zero, and it is never treated as the best value.
          </p>

          <div className="layout" style={{ gridTemplateColumns: '1fr' }}>
            <section className="panel">
              <h2 className="panel-title">Protein per dollar</h2>
              <RankingBarChart rows={rows} metric="proteinPerDollar" maxBars={10} />
            </section>

            <section className="panel">
              <h2 className="panel-title">Macronutrient split</h2>
              <div style={{ display: 'grid', gap: '0.85rem' }}>
                {rows.map((row) => (
                  <div key={row.product.id}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      {row.product.name}{' '}
                      <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                        {formatCurrency(row.metrics.values.price.value, row.metrics.currency)}
                      </span>
                    </div>
                    <MacroBar macros={row.metrics.macros} />
                  </div>
                ))}
              </div>
            </section>
          </div>
        </>
      )}
    </>
  );
}
